import {
  Send,
  CornerLeftUp,
  MoreHorizontal,
  ArrowUpToLine,
  ArrowUp,
  ArrowDown,
  ArrowDownToLine,
  Copy,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/hint";
import { cn } from "@/lib/utils";
import { previewMessage } from "@/lib/visual-editor/post-message";

interface FloatingToolbarProps {
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
    top: number;
    bottom: number;
    left: number;
    right: number;
  } | null;
  onAICommand: (command: string) => void;
  onSelectParent: () => void;
  onBringToFront?: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
  onSendToBack?: () => void;
  onDuplicate?: () => void;
  visible: boolean;
  iframeRef?: React.RefObject<HTMLIFrameElement>;
}

export function FloatingToolbar({
  bounds,
  onAICommand,
  onSelectParent,
  onBringToFront,
  onBringForward,
  onSendBackward,
  onSendToBack,
  onDuplicate,
  visible,
  iframeRef,
}: FloatingToolbarProps) {
  const [command, setCommand] = useState("");
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [hasEnoughSpace, setHasEnoughSpace] = useState(true);
  const [isHiddenDueToScroll, setIsHiddenDueToScroll] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Constants
  const TOOLBAR_WIDTH = 420;
  const TOOLBAR_HEIGHT = 44;
  const VIEWPORT_MARGIN = 16;
  const MIN_REQUIRED_WIDTH = TOOLBAR_WIDTH + VIEWPORT_MARGIN * 2; // 452px

  // Check if viewport has enough space for toolbar
  useEffect(() => {
    const checkViewportWidth = () => {
      const viewportWidth = window.innerWidth;
      const enoughSpace = viewportWidth >= MIN_REQUIRED_WIDTH;
      setHasEnoughSpace(enoughSpace);
    };

    checkViewportWidth();
    window.addEventListener("resize", checkViewportWidth);
    return () => window.removeEventListener("resize", checkViewportWidth);
  }, []);

  // Reset scroll-hidden state when toolbar becomes visible (new element selected)
  useEffect(() => {
    if (visible && bounds) {
      setIsHiddenDueToScroll(false);
    }
  }, [visible, bounds]);

  useEffect(() => {
    if (!bounds || !visible || !hasEnoughSpace) return;

    // Calculate toolbar position
    const gap = 50;

    // Get iframe offset if iframe reference is provided
    let iframeOffsetX = 0;
    let iframeOffsetY = 0;
    if (iframeRef?.current) {
      const iframeRect = iframeRef.current.getBoundingClientRect();
      iframeOffsetX = iframeRect.left;
      iframeOffsetY = iframeRect.top;
    }

    // Add iframe offset to bounds to convert from iframe coordinates to parent window coordinates
    let left =
      bounds.left + iframeOffsetX + bounds.width / 2 - TOOLBAR_WIDTH / 2;
    let top = bounds.bottom + iframeOffsetY + gap;

    // Keep within viewport horizontally
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    } else if (left + TOOLBAR_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - TOOLBAR_WIDTH - VIEWPORT_MARGIN;
    }

    // Keep within viewport vertically
    if (top + TOOLBAR_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
      // Position above element instead (also add iframe offset for consistency)
      top = bounds.top + iframeOffsetY - TOOLBAR_HEIGHT - gap;

      // If still not enough space, position at top of viewport
      if (top < VIEWPORT_MARGIN) {
        top = VIEWPORT_MARGIN;
      }
    }

    setPosition({ left, top });
  }, [bounds, visible, iframeRef, hasEnoughSpace]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (command.trim()) {
      onAICommand(command);
      setCommand("");
    }
  };

  // Hide toolbar on iframe scroll (using postMessage for cross-origin safety)
  useEffect(() => {
    if (!visible) return;

    const handleScrollMessage = (event: MessageEvent) => {
      // Only handle scroll events from the iframe
      if (event.data?.type === "scroll-position") {
        // Hide toolbar immediately during scroll
        setIsHiddenDueToScroll(true);
        previewMessage.clearSelection(iframeRef?.current || null);
        // Toolbar will stay hidden until user selects an element again
      }
    };

    window.addEventListener("message", handleScrollMessage);

    return () => {
      window.removeEventListener("message", handleScrollMessage);
    };
  }, [visible, iframeRef]);

  const isHidden =
    !visible || !bounds || !hasEnoughSpace || isHiddenDueToScroll;

  return (
    <div
      ref={toolbarRef}
      className={cn(
        "fixed z-[9999] flex items-center gap-2 rounded-lg bg-popover border border-border px-3 py-2 shadow-lg transition-opacity duration-150",
        isHidden && "pointer-events-none opacity-0",
      )}
      style={{
        width: `${TOOLBAR_WIDTH}px`,
        height: `${TOOLBAR_HEIGHT}px`,
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      {/* Input Container */}
      <div className="relative h-full flex-1 rounded-md bg-background/50 px-3">
        <form
          className="flex h-full w-full items-center"
          onSubmit={handleSubmit}
        >
          <input
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="Ask OptiDev ..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
          />
          <Hint text="Send to OptiDev (Enter)" side="top">
            <button
              type="submit"
              disabled={!command.trim()}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-md hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Send className="size-3.5" />
            </button>
          </Hint>
        </form>
      </div>

      {/* Divider */}
      <div className="h-5 w-[1px] rounded-full bg-border/50" />

      {/* Action Buttons */}
      <div className="flex shrink-0 items-center gap-1">
        <Hint delayDuration={100} text="Select Parent (↑)" side="top">
          <button
            onClick={onSelectParent}
            className="group relative inline-flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 w-8 h-8 text-muted-foreground hover:text-primary hover:bg-primary/10 active:scale-95"
            aria-label="Select parent element"
            type="button"
          >
            <CornerLeftUp className="size-4 transition-transform group-hover:scale-110" />
          </button>
        </Hint>

        <DropdownMenu>
          <Hint delayDuration={100} text="More Actions" side="top">
            <DropdownMenuTrigger asChild>
              <button
                className="group relative inline-flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 w-8 h-8 text-muted-foreground hover:text-primary hover:bg-primary/10 active:scale-95"
                aria-label="More actions"
                type="button"
              >
                <MoreHorizontal className="size-4 transition-transform group-hover:scale-110" />
              </button>
            </DropdownMenuTrigger>
          </Hint>
          <DropdownMenuContent align="end" sideOffset={8}>
            <DropdownMenuItem onClick={onBringToFront}>
              <ArrowUpToLine className="mr-2 size-4" />
              Bring to Front
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onBringForward}>
              <ArrowUp className="mr-2 size-4" />
              Bring Forward
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onSendBackward}>
              <ArrowDown className="mr-2 size-4" />
              Send Backward
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onSendToBack}>
              <ArrowDownToLine className="mr-2 size-4" />
              Send to Back
            </DropdownMenuItem>
            {onDuplicate && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDuplicate}>
                  <Copy className="mr-2 size-4" />
                  Duplicate
                  <span className="ml-auto pl-4 text-xs text-muted-foreground">
                    {/Mac|iPhone|iPad/.test(navigator.userAgent)
                      ? "\u2318C/V"
                      : "Ctrl+C/V"}
                  </span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
